import React, { useMemo, useState } from "react";
import {
  ActivityList,
  EmptyState,
  LoadingScreen,
  Metric,
  PageTitle,
  PortalShell,
  STATE_CHECKLIST,
  StatusPill,
  WorkflowBar,
  capacityMt,
  collectDocuments,
  dateTime,
  districtStatus,
  downloadCsv,
  hasSubsidyFile,
  human,
  isForwardedToState,
  isStateApproved,
  locationText,
  normal,
  number,
  projectId,
  projectName,
  registrarPackageUrl,
  runRegistrarAction,
  stageLabel,
  stateChecklistValue,
  stateStatus,
  subsidyPackage,
  updateRegistrarUser,
  uploadOfficialFile,
  upper,
  useRegistrarData,
  useRegistrarSession,
} from "./RegistrarPortalCore";

const INITIAL_FORM = {
  inwardNumber: "",
  inwardDate: "",
  pageCount: "",
  orderNumber: "",
  orderDate: "",
  dispatchNumber: "",
  dispatchDate: "",
  sanctionNumber: "",
  sanctionDate: "",
  sanctionAmount: "",
  remarks: "",
};

const ACTION_TITLES = {
  inward: "Record State Physical-File Inward",
  query: "Return File on State Query",
  reject: "Reject Project File",
  permission: "Issue State Project Permission",
  subsidyQuery: "Raise State Subsidy Query",
  subsidyForward: "Forward Subsidy File to Department",
  subsidySanction: "Record Subsidy Sanction",
};

// Kept local so this dashboard also works with the existing/older
// RegistrarPortalCore.js already present in the project.
const LOCAL_WIDGET_CSS = `
  .rg-document-control,.rg-automation{border-top:4px solid #19764a}.rg-document-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}.rg-document-grid article{display:grid;grid-template-columns:34px minmax(0,1fr) auto;align-items:center;gap:10px;padding:12px;border:1px solid #d5e0eb;border-radius:9px;background:#fff}.rg-document-grid article.ready{border-left:4px solid #19764a}.rg-document-grid article.pending{border-left:4px solid #c7d2dc;background:#f8fafc}.rg-document-grid article>i{width:29px;height:29px;border-radius:8px;display:grid;place-items:center;background:#e7f5ec;color:#19764a;font-style:normal;font-size:10px;font-weight:900}.rg-document-grid article.pending>i{background:#e9eef3;color:#73869a}.rg-document-grid article div{display:flex;min-width:0;flex-direction:column}.rg-document-grid article b{font-size:11px;color:#092f5b}.rg-document-grid article span{margin-top:4px;font-size:9px;color:#60748a}.rg-document-grid article a{border:1px solid #aabed0;border-radius:6px;padding:7px 8px;color:#1769b0;text-decoration:none;font-size:9px;font-weight:900;white-space:nowrap}.rg-document-grid article em{font-size:9px;color:#8b99a8;white-space:nowrap}.rg-automation-list{display:flex;flex-direction:column}.rg-automation-list article{display:grid;grid-template-columns:76px minmax(190px,1.15fr) minmax(220px,1.4fr) 86px auto;align-items:center;gap:13px;padding:12px 2px;border-bottom:1px solid #e2e9f0}.rg-automation-list article:last-child{border-bottom:0}.rg-automation-list article>div{display:flex;flex-direction:column}.rg-automation-list article b{font-size:11px;color:#092f5b}.rg-automation-list article small,.rg-automation-list article time{font-size:9px;color:#60748a}.rg-automation-list article p{font-size:11px;color:#425c73;margin:0}.rg-priority{display:inline-flex;justify-content:center;padding:6px 8px;border-radius:999px;background:#e7f0fb;color:#1c5a98;font-size:8px;font-weight:950}.rg-priority.high{background:#fff0d2;color:#945d08}.rg-priority.critical{background:#fde7e5;color:#a02f29}@media(max-width:720px){.rg-document-grid{grid-template-columns:1fr}.rg-document-grid article{grid-template-columns:30px minmax(0,1fr)}.rg-document-grid article a,.rg-document-grid article em{grid-column:2}.rg-automation-list article{grid-template-columns:70px minmax(0,1fr)}.rg-automation-list article p,.rg-automation-list article time,.rg-automation-list article button{grid-column:2}}
`;

const authorityDocuments = (project = {}) => {
  const source = collectDocuments(project);
  const definitions = [
    ["PACS Registrar Package", "PACS", /registrar.*(package|zip)|package.*registrar/i, true],
    ["PMC Final Signed DPR", "PMC", /dpr|detailed project report/i, true],
    ["Fully Executed PACS–PMC MOU", "PACS & PMC", /mou|memorandum/i, true],
    ["NAFED Assurance Letter", "NAFED / PMC", /assurance|hiring assurance/i, true],
    ["District Scrutiny & Forwarding Letter", "District Registrar", /district.*(forward|scrutiny)|forwarding/i, false],
    ["State Permission / Scrutiny Order", "State Registrar", /state.*(permission|scrutiny)|permission order/i, false],
    ["Signed DCDC Decision Letter", "District DCDC", /dcdc.*(decision|letter)/i, false],
  ];
  return definitions.map(([label, owner, pattern, required], index) => ({
    key: `${index}-${label}`,
    label,
    owner,
    required,
    document: source.find((item) =>
      pattern.test(`${item.name || ""} ${item.category || ""} ${item.path || ""}`),
    ),
  }));
};

const documentReadiness = (project = {}) => {
  const required = authorityDocuments(project).filter((item) => item.required);
  const complete = required.filter((item) => item.document).length;
  return { complete, total: required.length };
};

function DocumentControl({ project }) {
  const documents = authorityDocuments(project);
  const readiness = documentReadiness(project);
  return (
    <section className="rg-panel rg-document-control">
      <style>{LOCAL_WIDGET_CSS}</style>
      <div className="rg-panel-head">
        <div>
          <small>UNIFIED AUTHORITY DOCUMENT CONTROL</small>
          <h3>Official Project Document Matrix</h3>
          <p>PACS, PMC, District and State files from the same live project record.</p>
        </div>
        <StatusPill value={`${readiness.complete}/${readiness.total} mandatory ready`} />
      </div>
      <div className="rg-document-grid">
        {documents.map((item, index) => (
          <article className={item.document ? "ready" : "pending"} key={item.key}>
            <i>{item.document ? "✓" : String(index + 1).padStart(2, "0")}</i>
            <div><b>{item.label}</b><span>{item.owner} · {item.required ? "Mandatory" : "Workflow document"}</span></div>
            {item.document ? (
              <a href={item.document.url} target="_blank" rel="noreferrer">View / Download</a>
            ) : <em>Not available yet</em>}
          </article>
        ))}
      </div>
    </section>
  );
}

const actionAge = (value) => {
  const date = value?.toDate ? value.toDate() : value?.seconds ? new Date(value.seconds * 1000) : new Date(value || 0);
  const time = Number.isNaN(date.getTime()) ? Date.now() : date.getTime();
  return Math.max(0, Math.floor((Date.now() - time) / 86400000));
};

const nextAuthorityAction = (project, scope) => {
  const stateValue = upper(project.stateRegistrarStatus || project.registrarWorkflow?.state?.status);
  if (scope === "STATE") {
    if (/QUERY|REJECT/.test(stateValue)) return ["Compliance", "Track the query through District and allocated PMC", "queries", true];
    if (isForwardedToState(project) && project.stateRegistrarChecklistComplete !== true && !isStateApproved(project)) return ["10-Point Scrutiny", "Check complete ZIP and mandatory documents", "scrutiny", true];
    if (project.stateRegistrarChecklistComplete === true && !isStateApproved(project)) return ["Permission", "Upload and issue signed permission order", "permission", true];
    return ["Monitoring", "No State authority action is pending", "overview", false];
  }
  if (/QUERY|REJECT|ADVERSE/.test(districtStatus(project))) return ["Compliance", "Track PACS / PMC compliance", "queries", true];
  if (authorityDocuments(project)[0].document && !project.districtPhysicalFileReceived) return ["Inward", "Record District physical-file inward", "inbox", true];
  if (project.districtPhysicalFileReceived && project.districtRegistrarVerified !== true && !districtStatus(project).includes("VERIFIED")) return ["Scrutiny", "Complete document and adverse-status scrutiny", "scrutiny", true];
  if ((project.districtRegistrarVerified === true || districtStatus(project).includes("VERIFIED")) && !isForwardedToState(project)) return ["Dispatch", "Upload letter and forward to State Registrar", "forward", true];
  if (isStateApproved(project) && !project.dcdcMeetingDate) return ["DCDC", "Schedule DCDC meeting", "dcdc", true];
  if (isStateApproved(project) && project.dcdcMeetingDate && !["APPROVED", "REJECTED"].includes(upper(project.dcdcStatus))) return ["DCDC", "Record DCDC decision and signed letter", "dcdc", true];
  return ["Monitoring", "No District authority action is pending", "overview", false];
};

function AutomationQueue({ projects = [], scope, onOpen, limit = 8 }) {
  const rows = projects.map((project) => {
    const [queue, nextAction, actionKey, actionable] = nextAuthorityAction(project, scope);
    const ageDays = actionAge(project.updatedAt || project.createdAt);
    const priority = ageDays >= 7 ? "CRITICAL" : ageDays >= 3 ? "HIGH" : "DUE";
    return { project, queue, nextAction, actionKey, actionable, ageDays, priority };
  }).filter((item) => item.actionable).sort((a, b) => b.ageDays - a.ageDays).slice(0, limit);
  return (
    <section className="rg-panel rg-automation">
      <style>{LOCAL_WIDGET_CSS}</style>
      <div className="rg-panel-head"><div><small>AUTOMATED NEXT-ACTION ENGINE</small><h3>Priority Authority Queue</h3><p>Pending action and ageing are calculated from live project status.</p></div><StatusPill value={`${rows.length} priority files`} /></div>
      {rows.length ? <div className="rg-automation-list">{rows.map((item) => (
        <article key={item.project.id}><span className={`rg-priority ${normal(item.priority)}`}>{human(item.priority)}</span><div><b>{projectName(item.project)}</b><small>{projectId(item.project)} · {item.queue}</small></div><p>{item.nextAction}</p><time>{item.ageDays} day ageing</time><button className="rg-link" onClick={() => onOpen(item.project, item.actionKey)}>Open File →</button></article>
      ))}</div> : <EmptyState title="All authority queues are current" text="No actionable file is waiting in this jurisdiction." />}
    </section>
  );
}

function StateProjectRegister({
  title,
  description,
  rows,
  search,
  setSearch,
  district,
  setDistrict,
  districts,
  onOpen,
  emptyText,
}) {
  const filtered = rows.filter((project) => {
    const matchesSearch = normal(
      `${projectId(project)} ${projectName(project)} ${locationText(project)} ${project.assignedPmcName || project.pmcName}`,
    ).includes(normal(search));
    const matchesDistrict =
      district === "ALL" ||
      normal(project.district || project.districtName) === normal(district);
    return matchesSearch && matchesDistrict;
  });
  return (
    <section className="rg-panel">
      <div className="rg-panel-head">
        <div>
          <small>LIVE STATE REGISTER</small>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        <StatusPill value={`${filtered.length} files`} />
      </div>
      <div
        className="rg-filters"
        style={{ gridTemplateColumns: "1fr 220px auto" }}
      >
        <input
          className="rg-input"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search PACS, application, district, taluka or PMC..."
        />
        <select
          className="rg-select"
          value={district}
          onChange={(event) => setDistrict(event.target.value)}
        >
          <option value="ALL">All Districts</option>
          {districts.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
        <button
          className="rg-btn secondary"
          onClick={() => {
            setSearch("");
            setDistrict("ALL");
          }}
        >
          Clear
        </button>
      </div>
      {filtered.length ? (
        <div className="rg-table-wrap">
          <table className="rg-table">
            <thead>
              <tr>
                <th>Application / PACS</th>
                <th>District / Location</th>
                <th>Capacity</th>
                <th>District Dispatch</th>
                <th>Mandatory Documents</th>
                <th>State Stage</th>
                <th>Updated</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((project) => (
                <tr key={project.id}>
                  <td>
                    <b>{projectName(project)}</b>
                    <small>{projectId(project)}</small>
                  </td>
                  <td>{locationText(project)}</td>
                  <td>{number(capacityMt(project))} MT</td>
                  <td>
                    <b>{project.districtToStateDispatchNumber || "-"}</b>
                    <small>{project.districtToStateDispatchDate || ""}</small>
                  </td>
                  <td>
                    <StatusPill
                      value={`${documentReadiness(project).complete}/${documentReadiness(project).total} ready`}
                    />
                  </td>
                  <td>
                    <StatusPill value={stageLabel(project)} />
                  </td>
                  <td>{dateTime(project.updatedAt || project.createdAt)}</td>
                  <td>
                    <button className="rg-link" onClick={() => onOpen(project)}>
                      Open State File →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState title="No file in this State stage" text={emptyText} />
      )}
    </section>
  );
}

function StateCaseDrawer({
  project,
  checklist,
  setChecklist,
  onSaveChecklist,
  onAction,
  onClose,
  busy,
}) {
  if (!project) return null;
  const documents = collectDocuments(project);
  const completed = STATE_CHECKLIST.filter(([key]) => checklist[key]).length;
  const packageUrl = registrarPackageUrl(project);
  const subsidyUrl = subsidyPackage(project)?.url;
  return (
    <div className="rg-drawer-backdrop" onMouseDown={onClose}>
      <aside
        className="rg-drawer"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="rg-drawer-head">
          <div>
            <small>{projectId(project)}</small>
            <h2>{projectName(project)}</h2>
            <p>{locationText(project)}</p>
          </div>
          <button onClick={onClose}>×</button>
        </header>
        <div className="rg-drawer-body">
          <section className="rg-panel">
            <WorkflowBar project={project} />
            <div className="rg-summary">
              <div>
                <span>Capacity</span>
                <b>{number(capacityMt(project))} MT</b>
              </div>
              <div>
                <span>District Status</span>
                <b>{human(districtStatus(project))}</b>
              </div>
              <div>
                <span>State Status</span>
                <b>{human(stateStatus(project))}</b>
              </div>
              <div>
                <span>Checklist</span>
                <b>{completed}/10 complete</b>
              </div>
            </div>
          </section>

          <DocumentControl project={project} />

          {(project.stateRegistrarRemarks || project.districtStateQueryForwardingRemarks || project.pmcStateQueryComplianceResponse) && (
            <section className="rg-panel" style={{ borderLeft: "5px solid #d08a18" }}>
              <div className="rg-panel-head"><div><small>STATE QUERY CHAIN</small><h3>State → District → PMC Compliance Trail</h3><p>The State communicates with District; District routes the query and verified response.</p></div></div>
              <div className="rg-summary">
                <div><span>State Query</span><b>{project.stateRegistrarRemarks || "-"}</b></div>
                <div><span>District Forwarding</span><b>{project.districtStateQueryForwardingRemarks || "-"}</b></div>
                <div><span>PMC Response</span><b>{project.pmcStateQueryComplianceResponse || "-"}</b></div>
                <div><span>District Verification</span><b>{project.districtStateComplianceRemarks || "-"}</b></div>
              </div>
            </section>
          )}

          <section className="rg-panel">
            <div className="rg-panel-head">
              <div>
                <small>STATE MANDATORY SCRUTINY</small>
                <h3>10-Point Official Checklist</h3>
                <p>
                  State permission remains blocked until the District-forwarded
                  package and all ten checks are complete.
                </p>
              </div>
              <StatusPill value={`${completed}/10`} />
            </div>
            <div className="rg-checklist">
              {STATE_CHECKLIST.map(([key, label], index) => (
                <label key={key}>
                  <input
                    type="checkbox"
                    checked={checklist[key] === true}
                    disabled={
                      !isForwardedToState(project) ||
                      stateStatus(project).includes("RETURNED_TO_DISTRICT") ||
                      isStateApproved(project)
                    }
                    onChange={(event) =>
                      setChecklist((current) => ({
                        ...current,
                        [key]: event.target.checked,
                      }))
                    }
                  />
                  <span>
                    <b>{String(index + 1).padStart(2, "0")}</b> · {label}
                  </span>
                </label>
              ))}
            </div>
            {isForwardedToState(project) &&
              !stateStatus(project).includes("RETURNED_TO_DISTRICT") &&
              !isStateApproved(project) && (
                <div className="rg-actions">
                  <button
                    className="rg-btn green"
                    disabled={busy}
                    onClick={onSaveChecklist}
                  >
                    {busy ? "Saving..." : "Save State Checklist"}
                  </button>
                </div>
              )}
          </section>

          <section className="rg-panel">
            <div className="rg-panel-head">
              <div>
                <small>SERIAL DOCUMENT FILE</small>
                <h3>Registrar Package & Linked Evidence</h3>
                <p>View each live document or download the complete package.</p>
              </div>
              <aside>
                {packageUrl && (
                  <a
                    className="rg-btn secondary"
                    href={packageUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Registrar ZIP
                  </a>
                )}
                {subsidyUrl && (
                  <a
                    className="rg-btn secondary"
                    href={subsidyUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Subsidy ZIP
                  </a>
                )}
              </aside>
            </div>
            {documents.length ? (
              <div className="rg-doc-list">
                {documents.map((document) => (
                  <a
                    href={document.url}
                    key={document.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <b>{document.name}</b>
                    <span>{document.category} · View / Download</span>
                  </a>
                ))}
              </div>
            ) : (
              <EmptyState
                title="No linked documents"
                text="The District Registrar must forward a complete file."
              />
            )}
          </section>

          <section className="rg-panel">
            <div className="rg-panel-head">
              <div>
                <small>STATE AUTHORITY ACTION</small>
                <h3>Permission & Compliance Desk</h3>
                <p>Only valid next actions are shown for this file.</p>
              </div>
            </div>
            <div
              className="rg-actions"
              style={{ justifyContent: "flex-start" }}
            >
              {project.enableLegacyPhysicalInward === true &&
                isForwardedToState(project) &&
                !project.stateRegistrarPhysicalFileReceived && (
                  <button
                    className="rg-btn"
                    onClick={() => onAction("inward", project)}
                  >
                    Record Optional State Inward
                  </button>
                )}
              {isForwardedToState(project) &&
                !stateStatus(project).includes("RETURNED_TO_DISTRICT") &&
                !isStateApproved(project) && (
                  <>
                    <button
                      className="rg-btn orange"
                      onClick={() => onAction("query", project)}
                    >
                      Raise Query
                    </button>
                    <button
                      className="rg-btn red"
                      onClick={() => onAction("reject", project)}
                    >
                      Reject File
                    </button>
                  </>
                )}
              {isForwardedToState(project) &&
                completed === 10 &&
                !isStateApproved(project) && (
                  <button
                    className="rg-btn green"
                    onClick={() => onAction("permission", project)}
                  >
                    Issue Project Permission
                  </button>
                )}
              {hasSubsidyFile(project) && (
                <>
                  <button
                    className="rg-btn orange"
                    onClick={() => onAction("subsidyQuery", project)}
                  >
                    Subsidy Query
                  </button>
                  <button
                    className="rg-btn"
                    onClick={() => onAction("subsidyForward", project)}
                  >
                    Forward to Department
                  </button>
                  <button
                    className="rg-btn green"
                    onClick={() => onAction("subsidySanction", project)}
                  >
                    Record Sanction
                  </button>
                </>
              )}
            </div>
          </section>

          <section className="rg-panel">
            <div className="rg-panel-head">
              <div>
                <small>STATE FILE CONTROL</small>
                <h3>Inward, Permission & DCDC Record</h3>
              </div>
            </div>
            <div className="rg-summary">
              <div>
                <span>State Inward</span>
                <b>{project.stateRegistrarInwardNumber || "-"}</b>
              </div>
              <div>
                <span>State File No.</span>
                <b>{project.stateRegistrarFileNumber || "-"}</b>
              </div>
              <div>
                <span>Permission Order</span>
                <b>{project.statePermissionOrderNumber || "-"}</b>
              </div>
              <div>
                <span>Permission Date</span>
                <b>{project.statePermissionOrderDate || "-"}</b>
              </div>
              <div>
                <span>DCDC Status</span>
                <b>{human(project.dcdcStatus || "Pending")}</b>
              </div>
              <div>
                <span>DCDC Meeting</span>
                <b>{project.dcdcMeetingDate || "-"}</b>
              </div>
              <div>
                <span>Subsidy Status</span>
                <b>{human(project.stateSubsidyStatus || "Pending")}</b>
              </div>
              <div>
                <span>Next Authority</span>
                <b>{human(project.nextResponsibleRole || "State Registrar")}</b>
              </div>
            </div>
          </section>
        </div>
      </aside>
    </div>
  );
}

function StateActionModal({
  type,
  form,
  setForm,
  setFile,
  onClose,
  onSubmit,
  busy,
}) {
  if (!type) return null;
  const field = (key, label, inputType = "text") => (
    <div className="rg-field">
      <label>{label} *</label>
      <input
        className="rg-input"
        type={inputType}
        value={form[key]}
        onChange={(event) =>
          setForm((current) => ({ ...current, [key]: event.target.value }))
        }
      />
    </div>
  );
  return (
    <div className="rg-modal-backdrop" onMouseDown={onClose}>
      <div
        className="rg-modal"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <h2>{ACTION_TITLES[type]}</h2>
          <button onClick={onClose}>×</button>
        </header>
        <section>
          <div className="rg-form-grid">
            {type === "inward" && (
              <>
                {field("inwardNumber", "State inward / file number")}
                {field("inwardDate", "Inward date", "date")}
                {field("pageCount", "Physical page count", "number")}
              </>
            )}
            {type === "permission" && (
              <>
                {field("orderNumber", "State permission order number")}
                {field("orderDate", "Permission order date", "date")}
                <div className="rg-field">
                  <label>Signed permission order PDF *</label>
                  <input
                    className="rg-input"
                    type="file"
                    accept="application/pdf"
                    onChange={(event) =>
                      setFile(event.target.files?.[0] || null)
                    }
                  />
                </div>
              </>
            )}
            {["query", "reject"].includes(type) && (
              <div className="rg-field wide">
                <label>Signed State scrutiny / query order PDF (optional)</label>
                <input
                  className="rg-input"
                  type="file"
                  accept="application/pdf"
                  onChange={(event) => setFile(event.target.files?.[0] || null)}
                />
              </div>
            )}
            {type === "subsidyForward" && (
              <>
                {field("dispatchNumber", "Department dispatch / file number")}
                {field("dispatchDate", "Dispatch date", "date")}
              </>
            )}
            {type === "subsidySanction" && (
              <>
                {field("sanctionNumber", "Sanction order number")}
                {field("sanctionDate", "Sanction date", "date")}
                {field("sanctionAmount", "Sanctioned amount (₹)", "number")}
                <div className="rg-field">
                  <label>Sanction order PDF *</label>
                  <input
                    className="rg-input"
                    type="file"
                    accept="application/pdf"
                    onChange={(event) =>
                      setFile(event.target.files?.[0] || null)
                    }
                  />
                </div>
              </>
            )}
            <div className="rg-field wide">
              <label>Official remarks *</label>
              <textarea
                className="rg-textarea"
                value={form.remarks}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    remarks: event.target.value,
                  }))
                }
                placeholder="Write the permanent State authority remark..."
              />
            </div>
          </div>
          <div className="rg-actions">
            <button className="rg-btn secondary" onClick={onClose}>
              Cancel
            </button>
            <button className="rg-btn green" disabled={busy} onClick={onSubmit}>
              {busy ? "Saving..." : "Save State Action"}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

function RegistrarApprovalDesk({ registrars, onAction }) {
  return (
    <section className="rg-panel">
      <div className="rg-panel-head">
        <div>
          <small>STATE AUTHORITY ADMINISTRATION</small>
          <h2>District Registrar Approval Desk</h2>
          <p>
            Approve, query or reject District Registrar accounts within this
            State only.
          </p>
        </div>
        <StatusPill value={`${registrars.length} registrars`} />
      </div>
      {registrars.length ? (
        <div className="rg-table-wrap">
          <table className="rg-table">
            <thead>
              <tr>
                <th>Officer</th>
                <th>District</th>
                <th>Contact</th>
                <th>Status</th>
                <th>Updated</th>
                <th>Authority Action</th>
              </tr>
            </thead>
            <tbody>
              {registrars.map((registrar) => (
                <tr key={registrar.id}>
                  <td>
                    <b>
                      {registrar.name ||
                        registrar.fullName ||
                        "District Registrar"}
                    </b>
                    <small>
                      {registrar.designation ||
                        registrar.registrationNumber ||
                        registrar.id}
                    </small>
                  </td>
                  <td>{registrar.district || "-"}</td>
                  <td>{registrar.email || registrar.mobile || "-"}</td>
                  <td>
                    <StatusPill
                      value={
                        registrar.status ||
                        registrar.registrationStatus ||
                        "PENDING"
                      }
                    />
                  </td>
                  <td>
                    {dateTime(registrar.updatedAt || registrar.createdAt)}
                  </td>
                  <td>
                    <button
                      className="rg-link"
                      onClick={() => onAction(registrar, "APPROVED")}
                    >
                      Approve
                    </button>{" "}
                    ·{" "}
                    <button
                      className="rg-link"
                      onClick={() => onAction(registrar, "QUERY")}
                    >
                      Query
                    </button>{" "}
                    ·{" "}
                    <button
                      className="rg-link"
                      onClick={() => onAction(registrar, "REJECTED")}
                    >
                      Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState
          title="No District Registrar record"
          text="New State-linked registrations will appear here."
        />
      )}
    </section>
  );
}

export default function NafedStateRegistrarDashboard() {
  const {
    profile,
    loading: sessionLoading,
    error: sessionError,
  } = useRegistrarSession(["STATE_REGISTRAR", "STATE_REG"]);
  const { projects, users, news, loading, error } = useRegistrarData(
    profile,
    "STATE",
  );
  const [active, setActive] = useState("overview");
  const [search, setSearch] = useState("");
  const [district, setDistrict] = useState("ALL");
  const [selectedId, setSelectedId] = useState("");
  const [checklist, setChecklist] = useState({});
  const [actionType, setActionType] = useState("");
  const [actionProjectId, setActionProjectId] = useState("");
  const [form, setForm] = useState(INITIAL_FORM);
  const [file, setFile] = useState(null);
  const [registrarAction, setRegistrarAction] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const selectedProject =
    projects.find((project) => project.id === selectedId) || null;
  const actionProject =
    projects.find((project) => project.id === actionProjectId) || null;
  const districtRegistrars = users.filter((item) =>
    ["DISTRICT_REGISTRAR", "DISTRICT_REG"].includes(upper(item.role)),
  );
  const districts = useMemo(
    () =>
      [
        ...new Set(
          projects
            .map((project) => project.district || project.districtName)
            .filter(Boolean),
        ),
      ].sort(),
    [projects],
  );

  const stages = useMemo(() => {
    const inbox = projects.filter(
      (project) =>
        isForwardedToState(project) &&
        !/QUERY|REJECT/.test(stateStatus(project)) &&
        !isStateApproved(project),
    );
    const isExceptionFile = (project) =>
      /QUERY|REJECT/.test(stateStatus(project));
    const scrutiny = projects.filter(
      (project) =>
        isForwardedToState(project) &&
        project.stateRegistrarChecklistComplete !== true &&
        !isExceptionFile(project) &&
        !isStateApproved(project),
    );
    const queries = projects.filter(isExceptionFile);
    const permission = projects.filter(
      (project) =>
        isForwardedToState(project) &&
        project.stateRegistrarChecklistComplete === true &&
        !isExceptionFile(project) &&
        !isStateApproved(project),
    );
    const dcdc = projects.filter(isStateApproved);
    const subsidy = projects.filter(hasSubsidyFile);
    return { all: projects, inbox, scrutiny, queries, permission, dcdc, subsidy };
  }, [projects]);

  const metrics = useMemo(
    () => ({
      capacity: projects.reduce(
        (total, project) => total + capacityMt(project),
        0,
      ),
      districts: new Set(
        projects
          .map((project) => project.district || project.districtName)
          .filter(Boolean),
      ).size,
      permitted: projects.filter(isStateApproved).length,
      dcdcApproved: projects.filter(
        (project) => upper(project.dcdcStatus) === "APPROVED",
      ).length,
      pendingRegistrars: districtRegistrars.filter(
        (item) => upper(item.status || item.registrationStatus) !== "APPROVED",
      ).length,
    }),
    [districtRegistrars, projects],
  );

  const navigation = [
    { key: "overview", label: "State Overview" },
    { key: "all", label: "All PACS Applications", count: projects.length },
    { key: "inbox", label: "District Forwarded Files", count: stages.inbox.length },
    {
      key: "scrutiny",
      label: "10-Point Scrutiny",
      count: stages.scrutiny.length,
    },
    {
      key: "queries",
      label: "Query & Compliance",
      count: stages.queries.length,
    },
    {
      key: "permission",
      label: "Permission Orders",
      count: stages.permission.length,
    },
    { key: "dcdc", label: "DCDC Movement", count: stages.dcdc.length },
    { key: "activity", label: "Live State Activity", count: news.length },
    { key: "reports", label: "State MIS Reports" },
  ];

  const openProject = (project) => {
    setSelectedId(project.id);
    setChecklist(stateChecklistValue(project));
    setMessage("");
  };
  const openAction = (type, project) => {
    setActionType(type);
    setActionProjectId(project.id);
    setFile(null);
    setForm({
      ...INITIAL_FORM,
      inwardDate: new Date().toISOString().slice(0, 10),
      orderDate: new Date().toISOString().slice(0, 10),
      dispatchDate: new Date().toISOString().slice(0, 10),
      sanctionDate: new Date().toISOString().slice(0, 10),
    });
    setMessage("");
  };
  const requireValue = (value, label) => {
    if (!String(value || "").trim()) throw new Error(`${label} is required.`);
  };

  const saveChecklist = async () => {
    if (!selectedProject || !profile) return;
    setBusy(true);
    setMessage("");
    try {
      if (!isForwardedToState(selectedProject))
        throw new Error("District Registrar must forward the file before State scrutiny.");
      const completedCount = STATE_CHECKLIST.filter(
        ([key]) => checklist[key] === true,
      ).length;
      const complete = completedCount === STATE_CHECKLIST.length;
      await runRegistrarAction({
        project: selectedProject,
        profile,
        action: "STATE_REGISTRAR_DOCUMENT_SCRUTINY_STARTED",
        message: `${projectName(selectedProject)} State checklist saved: ${completedCount}/10 complete.`,
        patch: {
          stateRegistrarChecklist: checklist,
          stateRegistrarChecklistCompletedCount: completedCount,
          stateRegistrarChecklistTotal: STATE_CHECKLIST.length,
          stateRegistrarChecklistComplete: complete,
          stateRegistrarStatus: complete
            ? "STATE_CHECKLIST_COMPLETE_PERMISSION_PENDING"
            : "STATE_DOCUMENT_SCRUTINY_IN_PROGRESS",
          registrarWorkflowStage: complete
            ? "STATE_PERMISSION_ORDER_PENDING"
            : "STATE_DOCUMENT_SCRUTINY",
          nextResponsibleRole: "STATE_REGISTRAR",
          "registrarWorkflow.state.checklist": checklist,
          "registrarWorkflow.state.status": complete
            ? "STATE_CHECKLIST_COMPLETE_PERMISSION_PENDING"
            : "STATE_DOCUMENT_SCRUTINY_IN_PROGRESS",
        },
        targetRoles: ["DISTRICT_REGISTRAR"],
      });
      setMessage(
        `State checklist saved successfully: ${completedCount}/10 complete.`,
      );
    } catch (checklistError) {
      setMessage(checklistError.message || "Unable to save State checklist.");
    } finally {
      setBusy(false);
    }
  };

  const saveAction = async () => {
    if (!actionProject || !profile || !actionType) return;
    setBusy(true);
    setMessage("");
    try {
      let action = "";
      let description = "";
      let patch = {};
      let targetRoles = ["DISTRICT_REGISTRAR", "PACS", "PMC"];
      const nowIso = new Date().toISOString();
      if (actionType === "inward") {
        requireValue(form.inwardNumber, "State inward number");
        requireValue(form.inwardDate, "Inward date");
        requireValue(form.pageCount, "Page count");
        if (!isForwardedToState(actionProject))
          throw new Error("District dispatch is required before State inward.");
        action = "STATE_REGISTRAR_PHYSICAL_FILE_RECEIVED";
        description = `${projectName(actionProject)} received by State Registrar as inward ${form.inwardNumber}.`;
        patch = {
          stateRegistrarPhysicalFileReceived: true,
          stateRegistrarInwardNumber: form.inwardNumber.trim(),
          stateRegistrarFileNumber: form.inwardNumber.trim(),
          stateRegistrarInwardDate: form.inwardDate,
          stateRegistrarPhysicalPageCount: Number(form.pageCount),
          stateRegistrarStatus: "STATE_PHYSICAL_FILE_RECEIVED",
          registrarWorkflowStage: "STATE_DOCUMENT_SCRUTINY",
          nextResponsibleRole: "STATE_REGISTRAR",
          "registrarWorkflow.state.status": "STATE_PHYSICAL_FILE_RECEIVED",
          "registrarWorkflow.state.receivedAtIso": nowIso,
        };
      }
      if (actionType === "query") {
        requireValue(form.remarks, "State query");
        const scrutinyLetter = file
          ? await uploadOfficialFile({
              file,
              role: "STATE_REGISTRAR",
              uid: profile.id,
              projectId: actionProject.id,
              folder: "state_scrutiny_query",
            })
          : null;
        action = "STATE_REGISTRAR_PROJECT_QUERY_RAISED";
        description = `State Registrar query for ${projectName(actionProject)}: ${form.remarks.trim()}`;
        patch = {
          stateRegistrarStatus: "STATE_QUERY_RETURNED_TO_DISTRICT",
          stateRegistrarRemarks: form.remarks.trim(),
          stateScrutinyLetterUrl:
            scrutinyLetter?.url || actionProject.stateScrutinyLetterUrl || "",
          stateScrutinyLetterPath:
            scrutinyLetter?.path || actionProject.stateScrutinyLetterPath || "",
          stateQueryRaisedAtIso: nowIso,
          registrarWorkflowStage: "STATE_QUERY_RETURNED_TO_DISTRICT",
          nextResponsibleRole: "DISTRICT_REGISTRAR",
          "registrarWorkflow.state.status": "STATE_QUERY_RETURNED_TO_DISTRICT",
        };
        targetRoles = ["DISTRICT_REGISTRAR"];
      }
      if (actionType === "reject") {
        requireValue(form.remarks, "Rejection reason");
        const rejectionLetter = file
          ? await uploadOfficialFile({
              file,
              role: "STATE_REGISTRAR",
              uid: profile.id,
              projectId: actionProject.id,
              folder: "state_rejection_order",
            })
          : null;
        action = "STATE_REGISTRAR_PROJECT_REJECTED";
        description = `${projectName(actionProject)} rejected by State Registrar: ${form.remarks.trim()}`;
        patch = {
          stateRegistrarStatus:
            "STATE_REJECTED_RETURNED_TO_DISTRICT_AND_PMC",
          stateRegistrarRemarks: form.remarks.trim(),
          stateScrutinyLetterUrl:
            rejectionLetter?.url || actionProject.stateScrutinyLetterUrl || "",
          stateScrutinyLetterPath:
            rejectionLetter?.path || actionProject.stateScrutinyLetterPath || "",
          registrarWorkflowStage:
            "STATE_REJECTED_RETURNED_TO_DISTRICT_AND_PMC",
          nextResponsibleRole: "DISTRICT_REGISTRAR_PMC",
          "registrarWorkflow.state.status":
            "STATE_REJECTED_RETURNED_TO_DISTRICT_AND_PMC",
        };
      }
      if (actionType === "permission") {
        requireValue(form.orderNumber, "Permission order number");
        requireValue(form.orderDate, "Permission order date");
        requireValue(form.remarks, "Permission remarks");
        if (!isForwardedToState(actionProject))
          throw new Error("District Registrar forwarding is mandatory before permission.");
        if (actionProject.stateRegistrarChecklistComplete !== true)
          throw new Error(
            "Complete all 10 State checklist items before permission.",
          );
        const order = await uploadOfficialFile({
          file,
          role: "STATE_REGISTRAR",
          uid: profile.id,
          projectId: actionProject.id,
          folder: "project_permission",
        });
        action = "STATE_REGISTRAR_PROJECT_PERMISSION_GRANTED";
        description = `${projectName(actionProject)} granted State project permission under order ${form.orderNumber}.`;
        patch = {
          stateProjectPermissionGranted: true,
          stateRegistrarApprovedForDcdc: true,
          statePermissionOrderNumber: form.orderNumber.trim(),
          statePermissionOrderDate: form.orderDate,
          statePermissionOrderUrl: order.url,
          statePermissionOrderPath: order.path,
          stateRegistrarStatus:
            "STATE_APPROVED_RETURNED_TO_DISTRICT_FOR_DCDC",
          stateRegistrarRemarks: form.remarks.trim(),
          registrarWorkflowStage:
            "STATE_APPROVED_RETURNED_TO_DISTRICT_FOR_DCDC",
          nextResponsibleRole: "DISTRICT_REGISTRAR_DCDC",
          "registrarWorkflow.state.status":
            "STATE_APPROVED_RETURNED_TO_DISTRICT_FOR_DCDC",
          "registrarWorkflow.state.permissionGrantedAtIso": nowIso,
          "registrarWorkflow.state.permissionOrder": order,
        };
      }
      if (actionType === "subsidyQuery") {
        requireValue(form.remarks, "Subsidy query");
        action = "STATE_REGISTRAR_SUBSIDY_QUERY_RAISED";
        description = `State subsidy query for ${projectName(actionProject)}: ${form.remarks.trim()}`;
        patch = {
          stateSubsidyStatus: "QUERY",
          stateSubsidyRemarks: form.remarks.trim(),
          subsidyAuthorityStage: "STATE_QUERY_RETURNED_TO_DISTRICT",
          nextResponsibleRole: "DISTRICT_REGISTRAR",
        };
      }
      if (actionType === "subsidyForward") {
        requireValue(form.dispatchNumber, "Department dispatch number");
        requireValue(form.dispatchDate, "Dispatch date");
        requireValue(form.remarks, "Forwarding remarks");
        action = "STATE_REGISTRAR_SUBSIDY_FORWARDED_TO_DEPARTMENT";
        description = `${projectName(actionProject)} subsidy file forwarded to department under ${form.dispatchNumber}.`;
        patch = {
          stateSubsidyStatus: "FORWARDED_TO_STATE_DEPARTMENT",
          stateSubsidyDispatchNumber: form.dispatchNumber.trim(),
          stateSubsidyDispatchDate: form.dispatchDate,
          stateSubsidyRemarks: form.remarks.trim(),
          subsidyAuthorityStage: "FORWARDED_TO_STATE_DEPARTMENT",
          nextResponsibleRole: "STATE_DEPARTMENT",
        };
      }
      if (actionType === "subsidySanction") {
        requireValue(form.sanctionNumber, "Sanction number");
        requireValue(form.sanctionDate, "Sanction date");
        requireValue(form.sanctionAmount, "Sanctioned amount");
        requireValue(form.remarks, "Sanction remarks");
        const sanction = await uploadOfficialFile({
          file,
          role: "STATE_REGISTRAR",
          uid: profile.id,
          projectId: actionProject.id,
          folder: "subsidy_sanction",
        });
        action = "STATE_REGISTRAR_SUBSIDY_SANCTION_RECORDED";
        description = `${projectName(actionProject)} subsidy sanction ${form.sanctionNumber} recorded for ₹${number(form.sanctionAmount)}.`;
        patch = {
          stateSubsidyStatus: "SANCTIONED",
          subsidySanctionNumber: form.sanctionNumber.trim(),
          subsidySanctionDate: form.sanctionDate,
          subsidySanctionAmount: Number(form.sanctionAmount),
          subsidySanctionOrderUrl: sanction.url,
          subsidySanctionOrderPath: sanction.path,
          stateSubsidyRemarks: form.remarks.trim(),
          subsidyAuthorityStage: "SANCTIONED",
          nextResponsibleRole: "PACS_PMC",
        };
      }
      await runRegistrarAction({
        project: actionProject,
        profile,
        action,
        message: description,
        patch,
        targetRoles,
      });
      setMessage(
        "State authority action saved successfully with permanent audit trail.",
      );
      setActionType("");
      setFile(null);
    } catch (actionError) {
      setMessage(actionError.message || "Unable to save the State action.");
    } finally {
      setBusy(false);
    }
  };

  const saveRegistrarAction = async () => {
    if (!registrarAction || !profile) return;
    setBusy(true);
    setMessage("");
    try {
      requireValue(form.remarks, "Authority remarks");
      await updateRegistrarUser({
        registrar: registrarAction.registrar,
        profile,
        status: registrarAction.status,
        remarks: form.remarks.trim(),
      });
      setMessage(
        `District Registrar ${human(registrarAction.status)} successfully.`,
      );
      setRegistrarAction(null);
    } catch (actionError) {
      setMessage(actionError.message || "Unable to update District Registrar.");
    } finally {
      setBusy(false);
    }
  };

  const exportStateMis = () => {
    const rows = [
      [
        "Application ID",
        "PACS / Society",
        "Registration No.",
        "District",
        "Taluka",
        "Village",
        "Capacity MT",
        "PMC",
        "District Status",
        "State Status",
        "Checklist",
        "Permission Order",
        "DCDC Status",
        "Subsidy Status",
        "Updated",
      ],
    ];
    projects.forEach((project) =>
      rows.push([
        projectId(project),
        projectName(project),
        project.registrationNumber || project.pacsRegistrationNo,
        project.district || project.districtName,
        project.taluka || project.talukaName || project.block,
        project.village || project.villageName,
        capacityMt(project),
        project.assignedPmcName || project.pmcName,
        districtStatus(project),
        stateStatus(project),
        `${project.stateRegistrarChecklistCompletedCount || 0}/10`,
        project.statePermissionOrderNumber,
        project.dcdcStatus,
        project.stateSubsidyStatus || project.subsidyWorkflowStatus,
        dateTime(project.updatedAt || project.createdAt),
      ]),
    );
    downloadCsv(
      `State_Registrar_MIS_${profile.state}_${new Date().toISOString().slice(0, 10)}.csv`,
      rows,
    );
  };

  if (sessionLoading)
    return <LoadingScreen title="Loading State Registrar Control Centre" />;
  if (sessionError || !profile)
    return (
      <LoadingScreen
        error={sessionError || "State Registrar profile is unavailable."}
      />
    );

  const renderModule = () => {
    if (active === "overview")
      return (
        <>
          <PageTitle
            eyebrow="STATE GOVERNANCE · LIVE AUTHORITY CONTROL"
            title={`${profile.state || "State"} Registrar Command Centre`}
            description="See every PACS application in the State, scrutinise District-forwarded files, send queries back through District, issue permission and monitor the live DCDC result."
          />
          <section className="rg-hero">
            <div>
              <small>WORLD'S LARGEST GRAIN STORAGE PLAN</small>
              <h2>
                One live State register with complete documents, ZIP download,
                query compliance and DCDC visibility.
              </h2>
              <p>
                District and PMC updates appear automatically on the same PACS
                record with a permanent authority audit trail.
              </p>
            </div>
            <aside>
              <span>
                <b>{metrics.districts}</b>
                <small>Districts covered</small>
              </span>
              <span>
                <b>{number(metrics.capacity)} MT</b>
                <small>Proposed capacity</small>
              </span>
            </aside>
          </section>
          <section className="rg-metrics">
            <Metric
              label="State File Inbox"
              value={stages.inbox.length}
              note="District dispatch awaiting inward"
              tone="orange"
            />
            <Metric
              label="10-Point Scrutiny"
              value={stages.scrutiny.length}
              note="Checklist incomplete"
              tone="blue"
            />
            <Metric
              label="Permission Pending"
              value={stages.permission.length}
              note="Checklist complete"
              tone="purple"
            />
            <Metric
              label="Permission Granted"
              value={metrics.permitted}
              note="Returned for District DCDC"
              tone="green"
            />
            <Metric
              label="State Queries"
              value={stages.queries.length}
              note="District compliance pending"
              tone="orange"
            />
            <Metric
              label="All PACS Applications"
              value={projects.length}
              note="Complete live State register"
              tone="blue"
            />
            <Metric
              label="DCDC Approved"
              value={metrics.dcdcApproved}
              note="Independent registrar decision"
              tone="green"
            />
            <Metric
              label="DCDC In Process"
              value={stages.dcdc.length}
              note="Live District updates"
              tone="purple"
            />
          </section>
          <AutomationQueue
            projects={projects}
            scope="STATE"
            onOpen={(project, key) => {
              setActive(key);
              openProject(project);
            }}
          />
          <div className="rg-grid-2">
            <section className="rg-panel">
              <div className="rg-panel-head">
                <div>
                  <small>STATE PRIORITY QUEUE</small>
                  <h3>Authority Actions Requiring Attention</h3>
                  <p>Each queue is a separate workflow register.</p>
                </div>
              </div>
              <div className="rg-table-wrap">
                <table className="rg-table" style={{ minWidth: 0 }}>
                  <tbody>
                    {[
                      [
                        "District-forwarded files awaiting State scrutiny",
                        stages.inbox.length,
                        "inbox",
                      ],
                      [
                        "State files under 10-point scrutiny",
                        stages.scrutiny.length,
                        "scrutiny",
                      ],
                      [
                        "Queries awaiting District compliance",
                        stages.queries.length,
                        "queries",
                      ],
                      [
                        "Checklist-complete permission orders",
                        stages.permission.length,
                        "permission",
                      ],
                      ["State-approved files in DCDC", stages.dcdc.length, "dcdc"],
                    ].map(([label, count, key]) => (
                      <tr key={key}>
                        <td>
                          <b>{label}</b>
                        </td>
                        <td>
                          <StatusPill value={`${count} pending`} />
                        </td>
                        <td>
                          <button
                            className="rg-link"
                            onClick={() => setActive(key)}
                          >
                            Open Workspace →
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
            <section className="rg-panel">
              <div className="rg-panel-head">
                <div>
                  <small>LIVE STATE ACTIVITY</small>
                  <h3>Latest Governance Updates</h3>
                </div>
                <button
                  className="rg-link"
                  onClick={() => setActive("activity")}
                >
                  View all
                </button>
              </div>
              <ActivityList items={news} limit={7} />
            </section>
          </div>
        </>
      );
    const moduleMap = {
      all: [
        "All PACS Applications",
        "Complete live State list of all PACS applications with District, PMC, documents and authority status.",
        stages.all,
        "No PACS application is available in this State.",
      ],
      inbox: [
        "District Forwarded File Inbox",
        "Complete digital files formally scrutinised and forwarded by District Registrars.",
        stages.inbox,
        "No District file is waiting for State inward.",
      ],
      scrutiny: [
        "State 10-Point Document Scrutiny",
        "District-forwarded files whose mandatory State checklist is incomplete.",
        stages.scrutiny,
        "No State file is pending checklist scrutiny.",
      ],
      queries: [
        "State Query & Compliance Desk",
        "Only project files currently returned to the District Registrar for compliance.",
        stages.queries,
        "No State project query is pending.",
      ],
      permission: [
        "Project Permission Order Desk",
        "District-forwarded, 10/10 checklist-complete files awaiting a signed State permission order.",
        stages.permission,
        "No file is ready for a State permission order.",
      ],
      dcdc: [
        "DCDC Movement Monitor",
        "State-permitted cases returned to District for DCDC scheduling, decision and signed letter.",
        stages.dcdc,
        "No State-permitted DCDC case is available.",
      ],
      subsidy: [
        "State Work-Start Subsidy Desk",
        "District-verified survey, soil test, design, estimate and DPR packages for downstream subsidy action.",
        stages.subsidy,
        "No subsidy file is available for State action.",
      ],
    };
    if (moduleMap[active]) {
      const [title, description, rows, emptyText] = moduleMap[active];
      return (
        <>
          <PageTitle
            eyebrow="STATE REGISTRAR OFFICIAL WORKSPACE"
            title={title}
            description={description}
          />
          <StateProjectRegister
            title={title}
            description={description}
            rows={rows}
            search={search}
            setSearch={setSearch}
            district={district}
            setDistrict={setDistrict}
            districts={districts}
            onOpen={openProject}
            emptyText={emptyText}
          />
        </>
      );
    }
    if (active === "registrars")
      return (
        <>
          <PageTitle
            eyebrow="STATE REGISTRAR AUTHORITY"
            title="District Registrar Administration"
            description="State-scoped approval and account-control workspace."
          />
          <RegistrarApprovalDesk
            registrars={districtRegistrars}
            onAction={(registrar, status) => {
              setRegistrarAction({ registrar, status });
              setForm({ ...INITIAL_FORM, remarks: "" });
            }}
          />
        </>
      );
    if (active === "activity")
      return (
        <>
          <PageTitle
            eyebrow="PERMANENT STATE AUDIT"
            title="State Activity & Notification Trail"
            description="District, State, PACS and PMC workflow events across the approved State jurisdiction."
          />
          <section className="rg-panel">
            <ActivityList items={news} />
          </section>
        </>
      );
    return (
      <>
        <PageTitle
          eyebrow="STATE MANAGEMENT INFORMATION SYSTEM"
          title="State Registrar MIS Reports"
          description="Download a district-wise PACS register containing checklist, permission, DCDC and subsidy status."
          actions={
            <button className="rg-btn green" onClick={exportStateMis}>
              Download State MIS CSV
            </button>
          }
        />
        <section className="rg-metrics">
          <Metric
            label="Total Projects"
            value={projects.length}
            note="Live State applications"
          />
          <Metric
            label="Total Capacity"
            value={`${number(metrics.capacity)} MT`}
            note="Proposed storage"
            tone="green"
          />
          <Metric
            label="Permission Granted"
            value={metrics.permitted}
            note="Eligible for DCDC"
            tone="purple"
          />
          <Metric
            label="Districts Covered"
            value={metrics.districts}
            note="State jurisdiction"
            tone="orange"
          />
        </section>
        <StateProjectRegister
          title="Complete State Project Register"
          description="Every active NAFED project in the approved State jurisdiction."
          rows={projects}
          search={search}
          setSearch={setSearch}
          district={district}
          setDistrict={setDistrict}
          districts={districts}
          onOpen={openProject}
          emptyText="No NAFED project is available in this State."
        />
      </>
    );
  };

  return (
    <PortalShell
      authority="State Registrar Portal"
      subtitle="World's Largest Grain Storage Plan · State Permission & Governance"
      profile={profile}
      navigation={navigation}
      active={active}
      onNavigate={(key) => {
        setActive(key);
        setSelectedId("");
        setMessage("");
      }}
      news={news}
    >
      {(error || message) && (
        <div
          className={`rg-alert ${error || /unable|required|error/i.test(message) ? "error" : "success"}`}
        >
          {error || message}
        </div>
      )}
      {loading ? (
        <LoadingScreen title="Loading live State records" />
      ) : (
        renderModule()
      )}
      <StateCaseDrawer
        project={selectedProject}
        checklist={checklist}
        setChecklist={setChecklist}
        onSaveChecklist={saveChecklist}
        onAction={openAction}
        onClose={() => setSelectedId("")}
        busy={busy}
      />
      <StateActionModal
        type={actionType}
        form={form}
        setForm={setForm}
        setFile={setFile}
        onClose={() => setActionType("")}
        onSubmit={saveAction}
        busy={busy}
      />
      {registrarAction && (
        <div
          className="rg-modal-backdrop"
          onMouseDown={() => setRegistrarAction(null)}
        >
          <div
            className="rg-modal"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header>
              <h2>{human(registrarAction.status)} District Registrar</h2>
              <button onClick={() => setRegistrarAction(null)}>×</button>
            </header>
            <section>
              <div className="rg-alert">
                {registrarAction.registrar.name ||
                  registrarAction.registrar.fullName ||
                  registrarAction.registrar.email}{" "}
                · {registrarAction.registrar.district}
              </div>
              <div className="rg-field">
                <label>State authority remarks *</label>
                <textarea
                  className="rg-textarea"
                  value={form.remarks}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      remarks: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="rg-actions">
                <button
                  className="rg-btn secondary"
                  onClick={() => setRegistrarAction(null)}
                >
                  Cancel
                </button>
                <button
                  className="rg-btn green"
                  disabled={busy}
                  onClick={saveRegistrarAction}
                >
                  {busy ? "Saving..." : "Confirm Authority Action"}
                </button>
              </div>
            </section>
          </div>
        </div>
      )}
    </PortalShell>
  );
}
